import { useEffect, useRef, useState } from "react";

/** Animated count-up with ease-out cubic over 1.6s, triggers on viewport enter. */
export function useCountUp(target, { duration = 1600, decimals = 0 } = {}) {
    const ref = useRef(null);
    const [value, setValue] = useState(0);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!ref.current) return;
        const el = ref.current;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const start = performance.now();
                        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
                        const tick = (now) => {
                            const t = Math.min(
                                1,
                                (now - start) / duration,
                            );
                            const v = easeOutCubic(t) * target;
                            setValue(
                                decimals === 0
                                    ? Math.round(v)
                                    : Number(v.toFixed(decimals)),
                            );
                            if (t < 1) requestAnimationFrame(tick);
                            else setDone(true);
                        };
                        requestAnimationFrame(tick);
                        io.disconnect();
                    }
                });
            },
            { threshold: 0.4 },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [target, duration, decimals]);

    return { ref, value, done };
}

/** Format number with thousand separators while keeping LTR digits in RTL contexts. */
export function formatNumber(n, locale = "en-US") {
    if (n == null) return "";
    return n.toLocaleString(locale);
}
